import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, ArrowLeft } from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Button variant="ghost" onClick={() => navigate('/')} className="mr-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück
            </Button>
            <span className="text-2xl font-bold text-slate-900">DMV-Lernportal</span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge className="bg-red-500 text-white mb-4">🔥 Lifetime Promo - Nur für kurze Zeit!</Badge>
          <h1 className="text-5xl font-bold text-slate-900 mb-6">
            Lebenslanger Zugang für nur <span className="text-[#4ECDC4]">€49</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Statt €19/Monat - Spare über €2000! Keine versteckten Kosten, kein Abo.
          </p>
          <div className="flex justify-center items-center space-x-4 mb-8">
            <span className="text-3xl font-bold text-red-500 line-through">€228/Jahr</span>
            <span className="text-4xl font-bold text-[#4ECDC4]">€49 Einmalig</span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {/* Free Plan */}
          <Card className="border-2">
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Kostenlos</CardTitle>
              <div className="text-3xl font-bold">€0</div>
              <div className="text-slate-600">Für immer</div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm">3 Stories pro Tag</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm">A1-A2 Level</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm">Grundlegende Grammatik</span>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>
                Kostenlos starten
              </Button>
            </CardContent>
          </Card>

          {/* Lifetime Plan - Featured */}
          <Card className="border-4 border-[#4ECDC4] relative shadow-2xl">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-[#4ECDC4] text-white px-4 py-1">⭐ LIFETIME PROMO</Badge>
            </div>
            <CardHeader className="text-center pt-8">
              <CardTitle className="text-2xl text-[#4ECDC4]">Lifetime Access</CardTitle>
              <div className="text-4xl font-bold">€49</div>
              <div className="text-slate-600">Einmalige Zahlung</div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm font-medium">Unbegrenzte Stories</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm font-medium">Alle Level (A1-C2)</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm font-medium">Vollständige Grammatik</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm font-medium">KI-Bilder & Merkhilfen</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm font-medium">Offline Download</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm font-medium">Prioritäts-Support</span>
                </div>
              </div>
              <Button className="w-full bg-[#4ECDC4] hover:bg-[#45b7aa] text-white font-bold py-3">
                🔥 JETZT ZUGREIFEN - €49
              </Button>
              <p className="text-xs text-center text-slate-500">
                ⚡ Angebot endet in 24 Stunden
              </p>
            </CardContent>
          </Card>

          {/* Monthly Plan */}
          <Card className="border-2">
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Monatlich</CardTitle>
              <div className="text-3xl font-bold">€19</div>
              <div className="text-slate-600">pro Monat</div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm">Unbegrenzte Stories</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm">Alle Level (A1-C2)</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm">Vollständige Grammatik</span>
                </div>
                <div className="flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm">Standard Support</span>
                </div>
              </div>
              <Button variant="outline" className="w-full">
                Monatlich wählen
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Häufig gestellte Fragen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
            <div>
              <h3 className="font-bold text-slate-900 mb-2">Ist das wirklich lebenslanger Zugang?</h3>
              <p className="text-slate-600">Ja! Einmal zahlen, für immer nutzen. Keine Abo-Fallen.</p>
            </div>
            <div>
              <h3 className="font-bold text-slate-900 mb-2">Funktioniert es offline?</h3>
              <p className="text-slate-600">Mit dem Lifetime Plan kannst du Stories herunterladen.</p>
            </div>
            <div>
              <h3 className="font-bold text-slate-900 mb-2">Wie lange ist das Angebot gültig?</h3>
              <p className="text-slate-600">Nur noch 24 Stunden! Danach kostet es wieder €19/Monat.</p>
            </div>
            <div>
              <h3 className="font-bold text-slate-900 mb-2">Kann ich mein Geld zurückbekommen?</h3>
              <p className="text-slate-600">30 Tage Geld-zurück-Garantie, ohne Wenn und Aber.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;